import { Icon } from '@zapier/zinnia-icons';
import { clsx } from 'clsx';
import React from 'react';
import {
  Button,
  ButtonProps,
  Link,
  LinkProps,
  PressEvent,
} from 'react-aria-components';

import styles from './StatusChip.module.scss';

type BaseStatusChipProps = {
  /** Handler called when the press is released over the `StatusChip`. */
  onPress?: (event: PressEvent) => void;

  /** The size of the `StatusChip`. */
  size?: 'compact' | 'small' | 'medium';

  /** The visual style of the `StatusChip`. */
  variant?: 'success' | 'warning' | 'error';

  /**
   * Whether the `StatusChip` is disabled.
   * @default false
   */
  isDisabled?: boolean;
};

type ButtonStatusChipProps = BaseStatusChipProps & {
  href?: undefined;
  hrefLang?: undefined;
  children?: ButtonProps['children'];
  className?: string;
  style?: React.CSSProperties;
  target?: undefined;
};

type LinkStatusChipProps = BaseStatusChipProps & {
  /** The children of the `StatusChip`. A function may be provided to alter the children based on the chip's state. */
  children?: LinkProps['children'];

  /** The CSS `className` for the chip. */
  className?: string;

  /** A URL to link. When passed, `StatusChip` will be rendered as a Link instead of a button.  */
  href: string;

  /** Hints at the human language of the linked URL. Only used if `href` prop is passed. */
  hrefLang?: string;

  /** The inline style for the chip. */
  style?: React.CSSProperties;

  /** The target window. Only used if `href` prop is passed. */
  target?: LinkProps['target'];
};

export type StatusChipProps = ButtonStatusChipProps | LinkStatusChipProps;

function isLinkProps(props: StatusChipProps): props is LinkStatusChipProps {
  return typeof props.href === 'string';
}

/**
 * StatusChip is used to display the status of an element while offering a path
 * to know more about it.
 */
export const StatusChip = (
  // Props cannot be destructured because the discriminated union
  // (`ButtonStatusChipProps | LinkStatusChipProps`) relies on
  // `isLinkProps(props)` to narrow the type. Destructuring loses the
  // discriminant, so TypeScript can't determine which union member `children`
  // belongs to (ButtonProps['children'] vs LinkProps['children']).
  props: StatusChipProps
) => {
  const isDisabled = props.isDisabled ?? false;

  const renderIcon = () => {
    const iconSize =
      props.size === 'medium' ? 'var(--zds-size-20)' : 'var(--zds-size-16)';
    if (props.variant === 'warning') {
      return (
        <Icon
          name={isDisabled ? 'alertCircleFill' : 'alertCircleColor'}
          size={iconSize}
        />
      );
    }
    if (props.variant === 'error') {
      return (
        <Icon
          color={isDisabled ? 'GrayWarm6' : 'StatusErrorStronger'}
          name="formXCircle"
          size={iconSize}
        />
      );
    }
    return (
      <Icon
        color={isDisabled ? 'GrayWarm6' : 'StatusSuccessStronger'}
        name="formCheckCircle"
        size={iconSize}
      />
    );
  };

  if (isLinkProps(props)) {
    return (
      <Link
        className={clsx(styles.root, props.className)}
        data-size={props.size}
        data-variant
        data-zds
        isDisabled={isDisabled}
        href={props.href}
        hrefLang={props.hrefLang}
        onPress={props.onPress}
        target={props.target}
      >
        {(linkRenderProps) => (
          <>
            {renderIcon()}
            {typeof props.children === 'function'
              ? props.children(linkRenderProps)
              : props.children}
          </>
        )}
      </Link>
    );
  }

  return (
    <Button
      className={clsx(styles.root, props.className)}
      data-size={props.size}
      data-variant={props.variant}
      data-zds
      isDisabled={isDisabled}
      onPress={props.onPress}
      style={props.style}
    >
      {(buttonRenderProps) => (
        <>
          {renderIcon()}
          {typeof props.children === 'function'
            ? props.children(buttonRenderProps)
            : props.children}
        </>
      )}
    </Button>
  );
};
